import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import type { Observable } from 'rxjs';
import {
  BehaviorSubject,
  Subject,
  bufferWhen,
  debounceTime,
  filter,
  map,
  mergeMap,
  queueScheduler,
  tap,
} from 'rxjs';
import { Memoize } from 'typescript-memoize';

import { createProviderToken, filterBoolean } from '@core/common-legacy';
import { ReportFieldValue } from '@core/reports-types';
import { decodeFiltersQuery, encodeFiltersQuery, toQueryString } from '@core/reports-util';

import { ReportFiltersRepo } from './report-filters.repo';
import { ReportsRefreshService } from './reports-refresh.service';

type TranslationsCache = Record<string, Record<string, string>>;

const { provide, inject: injectLocalTranslations } = createProviderToken<TranslationsCache>(
  'ReportsFiltersLocalTranslations',
)();

export const provideReportsFiltersLocalTranslations = provide;
export type ReportsLocalFilterTranslations = TranslationsCache;

@Injectable()
export class ReportsFilterTranslationsService {
  private reportFiltersRepo = inject(ReportFiltersRepo);
  private reportsRefresh = inject(ReportsRefreshService);
  private localTranslations = injectLocalTranslations({ optional: true });

  private cache$ = new BehaviorSubject<TranslationsCache>({});
  private translationsFetchQueue$ = new Subject<{ group: string; id: string }>();
  private pendingParameterizedGroups = new Set<string>();

  constructor() {
    this.reportsRefresh.onRevisit$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.pendingParameterizedGroups.clear());
    if (this.localTranslations) {
      this.cache$.next(this.localTranslations);
    }
    this.translationsFetchQueue$
      .pipe(
        bufferWhen(() => this.translationsFetchQueue$.pipe(debounceTime(100, queueScheduler))),
        filter(buff => buff.length > 0),
        mergeMap(queue => this.loadFiltersTranslations(queue)),
      )
      .subscribe();
  }

  public addFilterTranslation(translation: { label: string; group: string; id: string }) {
    this.updateCache([{ name: translation.label, field: translation.group, id: translation.id }]);

    // Also cache under the API-normalized key: the API encodes group:value as a flat string,
    // then normalizes by splitting on the first colon. Tag display reads from the store
    // (post-API-response), so we need the translation available under both keys.
    const decoded = decodeFiltersQuery(
      encodeFiltersQuery([{ group: translation.group, value: translation.id, inverse: false }]),
    );
    if (decoded.length > 0 && decoded[0].group !== translation.group) {
      this.updateCache([
        { name: translation.label, field: decoded[0].group, id: decoded[0].value },
      ]);
    }
  }

  public translateFilterSync(groupId: string, id: string): string | null {
    const translations = this.cache$.getValue();

    return this.selectFiterTranslation(translations, groupId, id) || null;
  }

  @Memoize((groupId: string, id: string) => `${groupId}:${id}`)
  public translateFilter(groupId: string, id: string): Observable<string> {
    const cached = this.cache$.getValue();

    if (!cached[groupId] || !cached[groupId][id]) {
      this.fetchTranslations(groupId, id);
    }

    return this.cache$.pipe(
      map(cache => this.selectFiterTranslation(cache, groupId, id)),
      filterBoolean,
    );
  }

  private selectFiterTranslation(
    translations: TranslationsCache,
    groupId: string,
    id: string,
  ): string | undefined {
    if (translations[groupId] && translations[groupId][id]) {
      return translations[groupId][id];
    }
    return undefined;
  }

  private fetchTranslations(groupId: string, id: string): void {
    if (groupId.includes(':')) {
      this.fetchParameterizedGroupValues(groupId);
    } else {
      this.translationsFetchQueue$.next({ group: groupId, id });
    }
  }

  private fetchParameterizedGroupValues(groupId: string): void {
    if (this.pendingParameterizedGroups.has(groupId)) return;
    this.pendingParameterizedGroups.add(groupId);
    this.reportFiltersRepo
      .loadFieldValues({ fields: groupId, filters: null, query: null })
      .subscribe(values => {
        this.updateCache(values.map(v => ({ name: v.name, field: groupId, id: v.id })));
      });
  }

  private loadFiltersTranslations(items: Array<{ group: string; id: string }>) {
    const groups = items.reduce(
      (acc, s) => {
        if (!acc.includes(s.group)) {
          acc.push(s.group);
        }

        return acc;
      },
      <string[]>[],
    );

    return this.reportFiltersRepo
      .loadFieldValues({
        query: null,
        filters: toQueryString(items),
        fields: groups.join(','),
      })
      .pipe(tap(suggestions => this.updateCache(suggestions)));
  }

  private updateCache(suggestions: ReportFieldValue[]) {
    let cache = { ...this.cache$.getValue() };

    suggestions.forEach(({ name, field: groupId, id }) => {
      if (!groupId || !name) {
        return;
      }
      cache = {
        ...cache,
        [groupId]: {
          ...(cache[groupId] ?? {}),
          [id]: name,
        },
      };
    });

    this.cache$.next(cache);
  }
}
